import type { EducationContent } from '/index';

export default {
  topParas: [
    <>
      This category covers emissions from your business’s consumption of <strong>electricity, heat, and steam</strong>,
      along with any <strong>energy attribute certificates (EACs)</strong> applicable to the locations included within
      the boundaries of this carbon review.
    </>,
  ],
  exampleParas: [
    <>
      Emissions in this category include, for example, those from{' '}
      <strong>electricity used at your offices or warehouses</strong> to power lighting, IT equipment, heating, and
      cooling systems during the reporting period.
    </>,
  ],
  included: {
    points: [
      {
        hasBullet: true,
        title: <strong>Location name</strong>,
        paras: [
          {
            para: (
              <>
                A <strong>unique identifier</strong> that helps you distinguish between different locations.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Country of operation</strong>,
        paras: [
          {
            para: (
              <>
                The <strong>country</strong> in which the location operates. This information is used to apply the
                appropriate <strong>emission factor</strong> when calculating emissions for that location.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Address</strong>,
        paras: [
          {
            para: (
              <>
                The <strong>primary address</strong> of the location, including the state, province, region, or
                territory. This, combined with the country of operation, ensures the correct emission factor is applied
                to the location’s energy use.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Type of occupancy</strong>,
        paras: [
          {
            para: (
              <>
                Indicates whether your business <strong>solely occupies</strong> the location or{' '}
                <strong>shares tenancy</strong> with other businesses.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: (
          <>
            <strong>Energy meter situation</strong> (shared occupancy only)
          </>
        ),
        paras: [
          {
            para: (
              <>
                Specifies whether your business has a <strong>dedicated energy meter</strong> or shares a{' '}
                <strong>common meter</strong> with other tenants in the building.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: (
          <>
            <strong>Floor space</strong> (shared meter only)
          </>
        ),
        paras: [
          {
            para: (
              <>
                Includes both the <strong>total floor space</strong> of the location and the{' '}
                <strong>portion occupied by your business</strong>. These figures are used to estimate your business’s
                share of total energy consumption when a meter is shared
              </>
            ),
          },
        ],
      },
    ],
  },
  notIncluded: {
    points: [
      {
        hasBullet: true,
        title: <strong>Consumption data</strong>,
        paras: [
          {
            para: (
              <>
                Details on the <strong>type and amount of energy consumed</strong> (e.g., activity data or supplier
                intensity data), as well as any <strong>energy attribute certificates</strong> associated with each
                location, are covered separately under the consumption data section.
              </>
            ),
          },
        ],
      },
      {
        hasBullet: true,
        title: <strong>Energy attribute certificates</strong>,
        paras: [
          {
            para: (
              <>
                Information about{' '}
                <strong>
                  Power Purchase Agreements (PPAs), Renewable Energy Guarantees of Origin (REGOs), Guarantees of Origin
                  (GOs)
                </strong>
                , or any other certificates between third parties and your business should be entered under the energy
                attribute certificates section of this carbon review.
              </>
            ),
          },
        ],
      },
    ],
  },
} satisfies EducationContent;
